import React, { useEffect, useState } from "react";
import { api, apiErr, fmtMoney, fmtDate, API } from "../lib/api";
import { PageHeader } from "../components/ResourcePage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Upload, FileText, ExternalLink, Trash2 } from "lucide-react";
import { toast } from "sonner";

export function Documents() {
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ doc_type: "invoice", title: "", amount: 0, date: "", notes: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [delId, setDelId] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/documents"); setDocs(data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ doc_type: "invoice", title: "", amount: 0, date: "", notes: "" }); setFile(null); setOpen(true); };

  const save = async () => {
    if (!file) { toast.error("Please choose a file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("doc_type", form.doc_type); fd.append("title", form.title);
      fd.append("amount", form.amount || 0); fd.append("date", form.date);
      fd.append("notes", form.notes); fd.append("file", file);
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const doDelete = async () => {
    try { await api.delete(`/documents/${delId}`); toast.success("Deleted"); setDelId(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="fade-up">
      <PageHeader title="Documents" subtitle="Upload and store invoices, receipts and bills."
        action={<Button onClick={openNew} data-testid="doc-add"><Upload className="mr-1.5 h-4 w-4" />Upload</Button>} />

      <div className="rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Uploaded by</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="w-24 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {docs.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="py-12 text-center text-muted-foreground">No documents yet.</TableCell></TableRow>
            ) : docs.map((d) => (
              <TableRow key={d.id} data-testid="doc-row">
                <TableCell className="font-medium"><FileText className="mr-1.5 inline h-4 w-4 text-muted-foreground" />{d.title}</TableCell>
                <TableCell><Badge className="border-0 bg-secondary capitalize text-secondary-foreground">{d.doc_type}</Badge></TableCell>
                <TableCell className="text-sm">{fmtDate(d.date)}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{d.uploaded_by || "—"}</TableCell>
                <TableCell className="text-right num">{d.amount ? fmtMoney(d.amount) : "—"}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <a href={`${API}/files/${d.file_id}`} target="_blank" rel="noreferrer" data-testid="doc-view">
                      <Button variant="ghost" size="icon" className="h-8 w-8"><ExternalLink className="h-4 w-4" /></Button>
                    </a>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setDelId(d.id)} data-testid="doc-delete"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Upload document</DialogTitle>
            <DialogDescription>Attach an invoice, receipt or bill (image or PDF).</DialogDescription></DialogHeader>
          <div className="grid gap-3 py-2 sm:grid-cols-2">
            <div><Label className="text-xs">Type</Label>
              <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="doc-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["invoice", "receipt", "bill"].map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><Label className="text-xs">Amount (₦)</Label>
              <Input type="number" className="mt-1.5" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label className="text-xs">Title</Label>
              <Input className="mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="doc-title" placeholder="e.g. Feed supplier invoice — June" /></div>
            <div><Label className="text-xs">Date</Label>
              <Input type="date" className="mt-1.5" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div><Label className="text-xs">File</Label>
              <Input type="file" accept="image/*,application/pdf" className="mt-1.5 text-xs" onChange={(e) => setFile(e.target.files[0])} data-testid="doc-file" /></div>
            <div className="sm:col-span-2"><Label className="text-xs">Notes</Label>
              <Textarea className="mt-1.5" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="doc-save">{busy ? "Uploading…" : "Upload"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={() => setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this document?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive" data-testid="doc-confirm-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
